import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    TextField,
} from '@material-ui/core';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import type { AddKitTypeParams } from '../actions/admin/addKitType';
import addKitType from '../actions/admin/addKitType';
import type { UpdateKitTypeParams } from '../actions/admin/updateKitType';
import updateKitType from '../actions/admin/updateKitType';
import type { KitTypeCategory } from '../actions/getKitTypeCategories';
import type { KitType } from '../actions/getKitTypes';
import useStore from '../store/store';

interface EditKitTypeFormProps {
    open: boolean;
    onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
    kitType?: KitType;
    onKitTypeUpdated: () => void;
    isAddMode: boolean;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const EditKitTypeForm: React.FC<EditKitTypeFormProps> = ({
    open,
    onClose,
    kitType,
    onKitTypeUpdated,
    isAddMode,
}) => {
    const kitTypeCategories: KitTypeCategory[] = useStore(
        (state) => state.kitTypeCategories
    );
    const [available, setAvailable] = useState(
        isAddMode ? false : kitType?.Available === 1
    );
    const [formData, setFormData] = useState({
        type: '',
        description: '',
        turnaroundTime: '',
        requiredSample: '',
        transportTempo: '',
        providedReports: '',
        kitTypeCatalogueId: '',
    });
    const [kitTypeCategoryId, setKitTypeCategoryId] = useState<number>(1);
    const [trf, setTrf] = React.useState<File | undefined>(undefined);

    useEffect(() => {
        if (open && !isAddMode && kitType) {
            setAvailable(kitType.Available === 1);
            setFormData({
                type: kitType.Type || '',
                description: kitType.Description || '',
                turnaroundTime: kitType.TurnaroundTime || '',
                requiredSample: kitType.RequiredSample || '',
                transportTempo: kitType.TransportTempo || '',
                providedReports: kitType.ProvidedReports || '',
                kitTypeCatalogueId: kitType.KitTypeCatalogueId || '',
            });
            setKitTypeCategoryId(kitType.KitTypeCategoryId || 1);
        } else if (open && isAddMode) {
            setAvailable(false);
            setFormData({
                type: '',
                description: '',
                turnaroundTime: '',
                requiredSample: '',
                transportTempo: '',
                providedReports: '',
                kitTypeCatalogueId: '',
            });
            setKitTypeCategoryId(1);
        }
        setTrf(undefined);
    }, [open, isAddMode, kitType]);

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setAvailable(event.target.checked);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            if (!uploadedFile.name.endsWith('.pdf')) {
                alert('Please upload a valid PDF file');
                return;
            }
            setTrf(uploadedFile);
        }
    };

    const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
        onClose(event, 'backdropClick');
    };

    const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
        event.preventDefault();

        const {
            type,
            description,
            turnaroundTime,
            requiredSample,
            transportTempo,
            providedReports,
            kitTypeCatalogueId,
        } = formData;

        if (isAddMode && !type) {
            console.warn('Type is required');
            return;
        }

        try {
            if (isAddMode) {
                const data: AddKitTypeParams = {
                    kitTypeCategoryId,
                    type: type.trim(),
                    description: description.trim(),
                    turnaroundTime: turnaroundTime.trim(),
                    requiredSample: requiredSample.trim(),
                    transportTempo: transportTempo.trim(),
                    providedReports: providedReports.trim(),
                    kitTypeCatalogueId: kitTypeCatalogueId.trim(),
                    available,
                    trf,
                };
                await addKitType(data);
            } else {
                const data: UpdateKitTypeParams = {
                    kitTypeId: kitType?.KitTypeId ?? 0,
                    kitTypeCategoryId,
                    type: type.trim(),
                    description: description.trim(),
                    turnaroundTime: turnaroundTime.trim(),
                    requiredSample: requiredSample.trim(),
                    transportTempo: transportTempo.trim(),
                    providedReports: providedReports.trim(),
                    kitTypeCatalogueId: kitTypeCatalogueId.trim(),
                    available,
                    trf,
                };
                await updateKitType(data);
            }

            onKitTypeUpdated();
            onClose(event, 'escapeKeyDown');
        } catch (error) {
            console.error('Error saving kit type:', error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {isAddMode ? 'Add New Test Type' : 'Edit Test Type'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        id="type"
                        name="type"
                        label="Type"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="kitTypeCatalogueId"
                        name="kitTypeCatalogueId"
                        label="ID"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.kitTypeCatalogueId}
                        onChange={handleInputChange}
                        required
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="description"
                        name="description"
                        label="Description"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.description}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 65535,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="turnaroundTime"
                        name="turnaroundTime"
                        label="Turnaround Time"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.turnaroundTime}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="requiredSample"
                        name="requiredSample"
                        label="Required Sample"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.requiredSample}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 400,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="transportTempo"
                        name="transportTempo"
                        label="Transport Temperature"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.transportTempo}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 200,
                        }}
                    />
                    <TextField
                        margin="dense"
                        id="providedReports"
                        name="providedReports"
                        label="Provided Reports"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.providedReports}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        inputProps={{
                            maxLength: 600,
                        }}
                    />
                    <TextField
                        select
                        margin="dense"
                        id="kitTypeCategoryId"
                        label="Kit Type Category"
                        fullWidth
                        variant="outlined"
                        value={kitTypeCategoryId}
                        onChange={(e) =>
                            setKitTypeCategoryId(Number(e.target.value))
                        }
                    >
                        {kitTypeCategories.map((category: KitTypeCategory) => (
                            <MenuItem
                                key={category.KitTypeCategoryId}
                                value={category.KitTypeCategoryId}
                            >
                                {category.Name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <p>
                        <b>TRF:</b>
                        <Button
                            color="primary"
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            startIcon={<CloudUploadIcon />}
                            style={{
                                margin: '1rem',
                                borderRadius: '25px',
                                textTransform: 'none',
                                boxShadow: 'none',
                            }}
                        >
                            Upload
                            <VisuallyHiddenInput
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                multiple
                            />
                        </Button>
                    </p>
                    <p>{trf?.name}</p>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={available}
                                onChange={handleCheckboxChange}
                            />
                        }
                        label="Available"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button type="submit" disabled={isAddMode && !trf}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditKitTypeForm;
